import { Alert, Checkbox, Tooltip } from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormDatePicker, FormInputGroup, FormText } from 'components/form';
import { makeDateConsistent } from 'helpers/dates';
import { useFormContext } from 'react-hook-form';
import { FormSelectOption } from 'components/form/types';
import { FormAutocomplete } from 'components/form/FormAutocomplete/FormAutocomplete';
import { maxValidFill } from 'helpers/planning';
import { Help } from '@mui/icons-material';
import styles from './CreateDeliveryModal.module.scss';
import { DeviceLookup } from 'views/Planning/Planning';
import { useLazyGetSupplierDevicesEsQuery } from 'store/services';
import { StoredUser } from 'store/features/authentication';
import { ElasticsearchDeviceBodyConfig } from 'types/ElasticsearchQueries';

export const CreateDeliveryForm: FC<{
    userOptions: FormSelectOption[];
    user: StoredUser | null;
    viewAsSupplier?: string;
    devicesList: DeviceLookup;
    setDevicesList: React.Dispatch<React.SetStateAction<DeviceLookup>>;
    isPreResolveChecked: boolean;
    setIsPreResolveChecked: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
    userOptions,
    user,
    viewAsSupplier,
    setDevicesList,
    devicesList,
    isPreResolveChecked,
    setIsPreResolveChecked,
}) => {
    const { setValue, register } = useFormContext();
    const [shouldShowHelpText, setShouldShowHelpText] =
        useState<boolean>(false);

    const [deviceOptions, setDeviceOptions] = useState<FormSelectOption[]>([]);
    const [searchDebounceId, setSearchDebounceId] =
        useState<NodeJS.Timeout | null>(null);

    const [searchDeviceQuery, { isLoading }] =
        useLazyGetSupplierDevicesEsQuery();

    const [disableDeviceInput, setDisableDeviceInput] =
        useState<boolean>(false);

    const updateFormDefaults = useCallback(
        (deviceId: string) => {
            if (isPreResolveChecked) return;

            if (!deviceId) {
                setValue('device', '');
                setValue('plannedDate', '');
                setValue('plannedGallons', '');
                setValue('delivery_instructions', '');
                setValue('estimatedDate', '');
                setShouldShowHelpText(false);
                return;
            }

            const selectedDevice = devicesList?.[deviceId];

            if (selectedDevice) {
                if (selectedDevice.duf_7) {
                    setValue(
                        'estimatedDate',
                        makeDateConsistent(selectedDevice.duf_7)
                    );
                    setValue(
                        'plannedDate',
                        makeDateConsistent(selectedDevice.duf_7)
                    );
                }

                const plannedGals = maxValidFill(selectedDevice);
                if (!isNaN(plannedGals)) {
                    setValue('plannedGallons', plannedGals);
                }

                setValue(
                    'delivery_instructions',
                    selectedDevice.delivery_instructions ?? ''
                );
                setShouldShowHelpText(true);
            }
        },
        [devicesList, setValue, isPreResolveChecked]
    );

    // watch device to update default data
    register('device', {
        onChange: (e) => updateFormDefaults(e.target.value),
    });

    const onSearchChange = (searchTerm: string) => {
        if (searchDebounceId !== null) clearTimeout(searchDebounceId);
        if (searchTerm) {
            setSearchDebounceId(
                setTimeout(() => {
                    searchDevices(searchTerm);
                }, 500)
            );
        }
    };

    const searchDevices = (searchTerm: string) => {
        const supplierId = viewAsSupplier ?? user?.protected?.supplierId;
        if (user && supplierId) {
            const queryConfig: ElasticsearchDeviceBodyConfig = {
                limit: 10,
                search: searchTerm,
                sortField: 'name',
                searchFields: ['name'],
                isAdminUser: user.protected?.supplierId?.toString() === '0',
            };
            searchDeviceQuery({
                supplierId,
                userUid: user.uid,
                queryConfig,
            }).then((result) => {
                if (result?.data?.results) {
                    setDeviceOptions(
                        result.data.results.map((device) => ({
                            name: device.name,
                            value: device.deviceId,
                            disabled:
                                device?.lastActiveDelivery?.ticketDirectory ===
                                'active',
                        }))
                    );
                    const newDevicesList = {
                        ...devicesList,
                    };
                    result.data.results.forEach((device) => {
                        newDevicesList[device.deviceId] = device;
                    });

                    setDevicesList(newDevicesList);
                }
            });
        }
    };

    const helpText =
        'Some selections have been set automatically based on when we estimate the selected tank will reach 20% capacity.';

    // Handle case where only one device is available
    useEffect(() => {
        // if devicesList only has one device, set it as the default device and disable the input field
        if (devicesList && Object.keys(devicesList).length === 1) {
            const device = devicesList[Object.keys(devicesList)[0]];
            setValue('device', device.deviceId);
            updateFormDefaults(device.deviceId);
            setDisableDeviceInput(true);
        }
    }, [devicesList, setValue, updateFormDefaults]);

    return (
        <>
            <Checkbox
                checked={isPreResolveChecked}
                aria-label="Create past delivery"
                onChange={(e) => setIsPreResolveChecked(e.target.checked)}
            />{' '}
            Create past delivery
            <FormInputGroup>
                <FormAutocomplete
                    label="Monitor"
                    field="device"
                    options={[
                        { name: 'Please select a monitor', value: '' },
                        ...deviceOptions,
                    ]}
                    className={styles.createTicketInput}
                    onSearch={(searchTerm) => onSearchChange(searchTerm)}
                    isLoading={isLoading}
                    disabled={disableDeviceInput}
                />
                {!isPreResolveChecked && (
                    <FormDatePicker
                        field="estimatedDate"
                        disabled
                        className={styles.createTicketInput}
                        // label="Est. Delivery Date"
                    />
                )}
                {!isPreResolveChecked && (
                    <Tooltip
                        className={styles.estimationTooltip}
                        title="Suggested date of delivery based on our estimates of when the selected tank will reach 20% capacity."
                    >
                        <Help />
                    </Tooltip>
                )}
            </FormInputGroup>
            <FormInputGroup>
                <FormDatePicker
                    field="plannedDate"
                    onChange={() => setShouldShowHelpText(false)}
                    className={styles.createTicketInput}
                    label={
                        isPreResolveChecked ? 'Date Delivered' : 'Planned Date'
                    }
                />
                <FormText
                    field="plannedGallons"
                    isNumber
                    onChange={() => setShouldShowHelpText(false)}
                    className={styles.createTicketInput}
                    label={
                        isPreResolveChecked
                            ? 'Gallons Delivered'
                            : 'Est. Gallons'
                    }
                />
            </FormInputGroup>
            {!isPreResolveChecked && (
                <FormInputGroup>
                    <FormAutocomplete
                        label="Driver"
                        field="taggedUsers"
                        options={[
                            { name: 'Please select a driver', value: '' },
                            ...userOptions,
                        ]}
                        className={styles.createTicketInput}
                    />
                    <FormText
                        isTextArea
                        field="delivery_instructions"
                        onChange={() => setShouldShowHelpText(false)}
                        className={styles.createTicketInput}
                    />
                </FormInputGroup>
            )}
            {shouldShowHelpText && !isPreResolveChecked && (
                <Alert severity="info" className={styles.helpText}>
                    {helpText}
                </Alert>
            )}
        </>
    );
};
